import { RiskLevel } from '@equip.health/ui';

import { PatientGender } from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToChartPrimaryKeyMap } from '~/lib/constants/survey/SurveyNameToChartPrimaryKeyMap';
import { SurveyNameToSurveyKeyMap } from '~/lib/constants/survey/SurveyNameToSurveyKeyMap';
import { deriveAIMSScores } from '~/lib/util/chartScoreStrategies.util';
import { round } from '~/lib/util/clinicalProfile.util';
import {
  getAvgScoreOfQuestions,
  sumKeysOfJsonResultsObject,
  sumSpecificKeys,
  sumSurveyQuestions,
} from '~/lib/util/survey.util';
import { calculateCssrsSinceLastContactScoreAndRisk } from '~/lib/util/surveyBadge.util';

export const AssessmentChartDataScoreStrategyMap: Partial<
  Record<
    SurveyName,
    {
      deriveScores: (
        assessment: SurveyResponse,
        patientGender: string,
      ) => { riskLevel?: RiskLevel } & Record<string, string | number>;
    }
  >
> = {
  [SurveyName.PARDI_ARQ_SELF]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.PARDI_ARQ_SELF];

      const scores = {
        ['Severity of impact (S1)']: round(
          sumKeysOfJsonResultsObject(assessment.jsonResults, [
            `${qKey}19`,
            `${qKey}20`,
          ]) / 2,
        ),
        ['Sensory based avoidance (S2)']: round(
          sumKeysOfJsonResultsObject(assessment.jsonResults, [
            `${qKey}21`,
            `${qKey}22`,
            `${qKey}23`,
          ]) / 3,
        ),
        ['Lack of interest (S3)']: round(
          sumKeysOfJsonResultsObject(assessment.jsonResults, [
            `${qKey}24`,
            `${qKey}25`,
            `${qKey}26`,
          ]) / 3,
        ),
        ['Concern about aversive consequences (S4)']: round(
          sumKeysOfJsonResultsObject(assessment.jsonResults, [
            `${qKey}27`,
            `${qKey}28`,
            `${qKey}29`,
          ]) / 3,
        ),
      };

      return {
        ...scores,
        riskLevel: undefined,
      };
    },
  },
  [SurveyName.PHQ8]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.PHQ8];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey);
      let riskLevel: RiskLevel;
      if (score < 5) {
        riskLevel = RiskLevel.LOW;
      } else if (score < 10) {
        riskLevel = RiskLevel.MILD;
      } else if (score < 15) {
        riskLevel = RiskLevel.MODERATE;
      } else if (score < 20) {
        riskLevel = RiskLevel.MODERATELY_SEVERE;
      } else {
        riskLevel = RiskLevel.HIGH;
      }

      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.PHQ8]]: score,
        riskLevel,
      };
    },
  },
  [SurveyName.PHQ9]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.PHQ9];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey);
      let riskLevel: RiskLevel;
      if (score < 5) {
        riskLevel = RiskLevel.LOW;
      } else if (score < 10) {
        riskLevel = RiskLevel.MILD;
      } else if (score < 15) {
        riskLevel = RiskLevel.MODERATE;
      } else if (score < 20) {
        riskLevel = RiskLevel.MODERATELY_SEVERE;
      } else {
        riskLevel = RiskLevel.HIGH;
      }

      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.PHQ9]]: score,
        riskLevel,
      };
    },
  },
  [SurveyName.GAD7]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.GAD7];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey);
      let riskLevel: RiskLevel;
      if (score < 5) {
        riskLevel = RiskLevel.LOW;
      } else if (score < 10) {
        riskLevel = RiskLevel.MILD;
      } else if (score < 15) {
        riskLevel = RiskLevel.MODERATE;
      } else {
        riskLevel = RiskLevel.HIGH;
      }
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.GAD7]]: score,
        riskLevel,
      };
    },
  },
  [SurveyName.C_SSRS_SINCE_LAST_CONTACT]: {
    deriveScores: (assessment) => {
      const result = calculateCssrsSinceLastContactScoreAndRisk(
        JSON.parse(assessment.jsonResults),
      );
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.C_SSRS_SINCE_LAST_CONTACT]]:
          result.score,
        riskLevel: result.risk,
      };
    },
  },
  [SurveyName.AUDIT]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.AUDIT];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey, [
        `${qKey}pt_sex`,
        `${qKey}c_score`,
      ]);

      let riskLevel: RiskLevel;
      if (score < 8) {
        riskLevel = RiskLevel.LOW;
      } else if (score < 15) {
        riskLevel = RiskLevel.MEDIUM;
      } else {
        riskLevel = RiskLevel.HIGH;
      }

      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.AUDIT]]: score,
        riskLevel,
      };
    },
  },
  [SurveyName.DUDIT]: {
    deriveScores: (assessment, patientGender) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.DUDIT];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey, [
        `${qKey}c_score`,
      ]);
      let riskLevel = RiskLevel.NOT_CALCULATED;

      if (patientGender) {
        const lowerCaseGender = patientGender.toLowerCase();
        const {
          CIS_GENDER_FEMALE,
          CIS_GENDER_MALE,
          NON_BINARY,
          OTHER,
          TRANSGENDER_FEMALE,
          TRANSGENDER_MALE,
          UNKNOWN,
        } = PatientGender;

        if (score >= 25) {
          riskLevel = RiskLevel.HIGH;
        } else if (lowerCaseGender === CIS_GENDER_MALE.toLowerCase()) {
          if (score < 6) {
            riskLevel = RiskLevel.LOW;
          } else {
            riskLevel = RiskLevel.MEDIUM;
          }
        } else if (lowerCaseGender === CIS_GENDER_FEMALE.toLowerCase()) {
          if (score < 2) {
            riskLevel = RiskLevel.LOW;
          } else {
            riskLevel = RiskLevel.MEDIUM;
          }
        } else if (
          [
            TRANSGENDER_MALE.toLowerCase(),
            TRANSGENDER_FEMALE.toLowerCase(),
            NON_BINARY.toLowerCase(),
            OTHER.toLowerCase(),
            UNKNOWN.toLowerCase(),
          ].includes(lowerCaseGender)
        ) {
          if (score < 5) {
            riskLevel = RiskLevel.LOW;
          } else {
            riskLevel = RiskLevel.MEDIUM;
          }
        }
      }
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.DUDIT]]: score,
        riskLevel,
      };
    },
  },
  [SurveyName.FNS]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.FNS];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey);
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.FNS]]: score,
      };
    },
  },
  [SurveyName.EDE_Q]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.EDE_Q];
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.EDE_Q]]:
          getAvgScoreOfQuestions(assessment.jsonResults, [
            `${qKey}1`,
            `${qKey}2`,
            `${qKey}3`,
            `${qKey}4`,
            `${qKey}5`,
            `${qKey}6`,
            `${qKey}7`,
            `${qKey}8`,
            `${qKey}8`,
            `${qKey}9`,
            `${qKey}10`,
            `${qKey}11`,
            `${qKey}12`,
            `${qKey}19`,
            `${qKey}20`,
            `${qKey}21`,
            `${qKey}22`,
            `${qKey}23`,
            `${qKey}24`,
            `${qKey}25`,
            `${qKey}26`,
            `${qKey}27`,
            `${qKey}28`,
          ]),
        ['Restraint (S1)']: getAvgScoreOfQuestions(assessment.jsonResults, [
          `${qKey}1`,
          `${qKey}2`,
          `${qKey}3`,
          `${qKey}4`,
          `${qKey}5`,
        ]),
        ['Eating concern (S2)']: getAvgScoreOfQuestions(
          assessment.jsonResults,
          [`${qKey}7`, `${qKey}9`, `${qKey}19`, `${qKey}21`, `${qKey}20`],
        ),
        ['Shape concern (S3)']: getAvgScoreOfQuestions(assessment.jsonResults, [
          `${qKey}6`,
          `${qKey}8`,
          `${qKey}10`,
          `${qKey}11`,
          `${qKey}23`,
          `${qKey}26`,
          `${qKey}27`,
          `${qKey}28`,
        ]),
        ['Weight concern (S4)']: getAvgScoreOfQuestions(
          assessment.jsonResults,
          [`${qKey}8`, `${qKey}12`, `${qKey}22`, `${qKey}24`, `${qKey}25`],
        ),
      };
    },
  },
  [SurveyName.CET]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.CET];
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.CET]]: sumSurveyQuestions(
          assessment.jsonResults,
          qKey,
        ),
        ['Avoidance and rule-driven behavior (S1)']: sumSpecificKeys(
          assessment.jsonResults,
          [
            `${qKey}9`,
            `${qKey}10`,
            `${qKey}11`,
            `${qKey}15`,
            `${qKey}16`,
            `${qKey}20`,
            `${qKey}22`,
            `${qKey}23`,
          ],
        ),
        ['Weight-control exercise (S2)']: sumSpecificKeys(
          assessment.jsonResults,
          [`${qKey}2`, `${qKey}6`, `${qKey}8`, `${qKey}13`, `${qKey}18`],
        ),
        ['Mood improvement (S3)']: sumSpecificKeys(assessment.jsonResults, [
          `${qKey}1`,
          `${qKey}4`,
          `${qKey}14`,
          `${qKey}17`,
          `${qKey}24`,
        ]),
        ['Lack of exercise enjoyment (S4)']: sumSpecificKeys(
          assessment.jsonResults,
          [`${qKey}5`, `${qKey}12`, `${qKey}21`],
        ),
        ['Exercise rigidity (S5)']: sumSpecificKeys(assessment.jsonResults, [
          `${qKey}3`,
          `${qKey}7`,
          `${qKey}19`,
        ]),
      };
    },
  },
  [SurveyName.AIMS]: {
    deriveScores: (assessment) => {
      return deriveAIMSScores(assessment.jsonResults);
    },
  },
  [SurveyName.ARFID]: {
    deriveScores: (assessment) => {
      const resultsObj = JSON.parse(assessment.jsonResults);
      return {
        ['Fear']: resultsObj['food-emotions-instances'],
        ['Reduce supplement/tube feed']: resultsObj['reduction-instances'],
        ['Arguments']: resultsObj['arguments-instances'],
        ['Increase intake']: resultsObj['intake-instances'],
        ['Trying new things']: resultsObj['trying-things-instances'],
        ['Foods from all groups']: resultsObj['food-groups-instances'],
        ['Social eating']: resultsObj['social-eating-instances'],
      };
    },
  },
  [SurveyName.NON_ARFID]: {
    deriveScores: (assessment) => {
      const resultsObj = JSON.parse(assessment.jsonResults);
      return {
        ['Purging']: resultsObj['purge-instances'],
        ['Binging']: resultsObj['binge-instances'],
        ['Fasting']: resultsObj['time-without-eating-instances'],
        ['Maladaptive exercise']: resultsObj['maladaptive-exercise-instances'],
        ['Control loss']: resultsObj['lose-control-instances'],
        ['Restricting']: resultsObj['eat-less-instances'],
        ['Change diet']: resultsObj['cut-food-instances'],
      };
    },
  },
  [SurveyName.QUALITY_OF_LIFE]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.QUALITY_OF_LIFE];
      const personalImpairment = sumSpecificKeys(assessment.jsonResults, [
        `${qKey}_critical_of_self`,
        `${qKey}_upset`,
        `${qKey}_ashamed`,
        `${qKey}_guilt`,
        `${qKey}_failure`,
        `${qKey}_worry`,
      ]);
      const socialImpairment = sumSpecificKeys(assessment.jsonResults, [
        `${qKey}_stopped_going_out`,
        `${qKey}_interfered_social_meals`,
        `${qKey}_difficulty_eating_out`,
        `${qKey}_interfered_joyful_activities`,
        `${qKey}_interfered_relationships`,
      ]);
      let cognitiveImpairment = sumSpecificKeys(assessment.jsonResults, [
        `${qKey}_difficulty_concentrating`,
        `${qKey}_forgetful`,
        `${qKey}_decision_making`,
        `${qKey}_absent_minded`,
      ]);
      // work performance has a N/A option that should be counted as 0
      const jsonObj = JSON.parse(assessment.jsonResults);
      const workPerf = jsonObj[`${qKey}_work_performance`];
      if (workPerf !== 'none') {
        cognitiveImpairment += parseInt(workPerf, 10);
      }

      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.QUALITY_OF_LIFE]]:
          personalImpairment + socialImpairment + cognitiveImpairment,
        ['Personal impairment']: personalImpairment,
        ['Social impairment']: socialImpairment,
        ['Cognitive impairment']: cognitiveImpairment,
      };
    },
  },
  [SurveyName.PCL5]: {
    deriveScores: (assessment) => {
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.PCL5];
      const score = sumSurveyQuestions(assessment.jsonResults, qKey);
      return {
        [SurveyNameToChartPrimaryKeyMap[SurveyName.PCL5]]: score,
      };
    },
  },
};
